import React, { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Alert, Box } from '@mui/material';

import Spinner from '~/components/Common/Spinner';
import PageBody from '~/components/Page/PageBody';
import PageHeaderBar from '~/components/Page/PageHeaderBar';
import PageLayout from '~/components/Page/PageLayout';
import PageSidebar from '~/components/Page/PageSidebar';

import useCurrentUser from '~/hooks/useCurrentUser';

import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';
import VendorsService from '~/services/VendorsService';

import { isAdmin } from '~/utils/adminPermission';

import OpportunityHeaderActions from './OpportunityHeaderActions';
import OpportunityHeaderTitle from './OpportunityHeaderTitle';
import OpportunityPage from './OpportunityPage';
import OpportunityPropertyInfo from './OpportunityPropertyInfo/OpportunityPropertyInfo';
import OpportunitySidebar from './OpportunitySidebar/OpportunitySidebar';

export default function OpportunityContainer() {
  const { user: currentUser } = useCurrentUser();
  const isAdminRole = useMemo(() => isAdmin(currentUser), [currentUser]);

  const [isLoading, setIsLoading] = useState(true);

  const { opportunityId } = useParams<{ opportunityId: string }>();

  const [opportunitiesResponse, setOpportunitiesResponse] = useState<App.OpportunityPage>(null);
  const [dealsResponse, setDealsResponse] = useState<Array<App.Deal>>([]);
  const [accountResponse, setAccountResponse] = useState<App.OfferAccount>(null);
  const [offerResponse, setOfferResponse] = useState<App.Offer>(null);
  const [propertyResponse, setPropertyResponse] = useState<App.Property>(null);

  const propertyId = useMemo(() => offerResponse?.packages[0]?.fk_property_id, [offerResponse]);

  useEffect(() => {
    const fetchData = async () => {
      if (!opportunityId) {
        return;
      }

      setIsLoading(true);

      try {
        const [opportunityResult, dealsResult, offerResult] = await Promise.all([
          OffersService.getOpportunity(opportunityId),
          OffersService.getDeals(opportunityId),
          OffersService.getOffer(opportunityId),
        ]);

        setOpportunitiesResponse(opportunityResult.result);
        setDealsResponse(dealsResult.result);
        setOfferResponse(offerResult.result);

        if (offerResult.result.vendor_account_id) {
          const accountResult = await VendorsService.getVendorById(offerResult.result.vendor_account_id);
          setAccountResponse(accountResult.result);
        }

        if (propertyId) {
          const propertyResult = await ReservationService.getProperty(propertyId);
          setPropertyResponse(propertyResult.result);
        }
      } catch (err) {
        throw new Error('opportunity data loading error:' + err.toString());
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [opportunityId, propertyId]);

  const { opportunity_info, key_contacts, partnership_goals, location } = opportunitiesResponse || {};

  if (isLoading) {
    return (
      <PageLayout hasLeftSidebar hasRightSidebar>
        <PageHeaderBar title="Opportunity Details" />
        <PageBody>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Spinner />
          </Box>
        </PageBody>
      </PageLayout>
    );
  }

  return (
    <PageLayout hasLeftSidebar hasRightSidebar>
      <PageHeaderBar
        title={<OpportunityHeaderTitle title={opportunity_info?.name} />}
        banner={
          offerResponse.customer_experience_notes ? (
            <Box mt={1}>
              <Alert severity="warning" variant="filled">
                <b>Important property notes</b> {offerResponse.customer_experience_notes}
              </Alert>
            </Box>
          ) : undefined
        }
        actions={<OpportunityHeaderActions vendorId={offerResponse?.vendor_account_id} propertyId={propertyId} />}
      />
      <PageSidebar placement="start" width="medium">
        <OpportunitySidebar opportunityId={opportunityId} />
      </PageSidebar>
      <PageBody>
        <OpportunityPage
          opportunityResponse={opportunitiesResponse}
          isAdminRole={isAdminRole}
          dealsResponse={dealsResponse}
          offerResponse={offerResponse}
          vendorId={offerResponse?.vendor_account_id}
          offerId={opportunityId}
          accountName={accountResponse?.name}
        />
      </PageBody>
      <PageSidebar placement="end" width="medium">
        <OpportunityPropertyInfo
          account={accountResponse}
          usp={partnership_goals?.usp__c}
          location={location}
          keyContacts={key_contacts}
          property={propertyResponse}
        />
      </PageSidebar>
    </PageLayout>
  );
}
